import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FormControl,
    FormControlLabel,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    Card,
    CardContent,
    Grid,
    Tooltip,
    CircularProgress,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import {
    updateCheckedProposableShifts,
} from '../../actions';
import { ExpandMore, WarningRounded as WarningIcon, Help as HelpIcon } from '@material-ui/icons';
import ProposableShiftInformation from './ProposableShiftInformation';
import ProposableShiftDateTime from './ProposableShiftDateTime';
import { SliderPartialRequestWithLabel } from '../SliderPartialRequest';
import { RequestTypes } from '../../../Enums';
import './ProposableShiftsAccordion.scss';

const IconLeftAccordionSummary = withStyles({
    expandIcon: {
        order: -1,
    },
    root: {
        borderBottom: '1px solid rgba(0, 0, 0, .125)',
        marginBottom: -1,
        minHeight: 56,
        '&$expanded': {
            minHeight: 56,
        },
    },
    content: {
        justifyContent: 'space-between',
        alignItems: 'center',
        '&$expanded': {
            margin: '12px 0',
        }
    },
    expanded: {},
})(AccordionSummary);
/**
 * 
 * @typedef {import('moment-timezone').Moment} Moment
 * @typedef {{ firstName: string, lastName: string, employeeId: number, onShiftChecked: (checked: boolean, shiftId: number, employee: Employee) => void }} Employee
 * @typedef {{ qualifier: string, id: number }} OrgJobRef
 * @typedef {{ type: string, orgJobRef: OrgJobRef }} Segment
 * @typedef {{ startDate: string, endDate: string, id: number, segments: Segment[] }} Shift
 * @typedef {{ storeData: StoreData, shifts: Shift[] }} SliderPartialRequestData
 * @param {{
 *  employee: Employee,
 *  sliderPartialRequestData: SliderPartialRequestData,
 *  selectedPartialStartDate: Moment?,
 *  selectedPartialEndDate: Moment?,
 *  isReadOnly: boolean?,
 * }} props 
 * @description This component was made to be used with 
 */
function ProposableShiftsAccordion(props) {
    const {
        employee,
        selectedPartialStartDate = null,
        selectedPartialEndDate = null,
        isReadOnly = false,
    } = props;
    const dispatch = useDispatch();
    const { selectedProposableShiftsByOfferor, proposalPartialDates } = useSelector((state) => state);
    const { employee: checkedEmployee, shifts: selectedShifts } = selectedProposableShiftsByOfferor;
    const selectedEmployeeId = checkedEmployee.employeeId;
    const offeredShiftsCount = employee.offeredShiftsCount;
    const isCheckboxDisabled = selectedShifts?.length >= offeredShiftsCount;//If n shifts offered then n shifts need be proposed
    const selectedPartialLabel = proposalPartialDates.partialShiftLabel;

    const isChecked = (shift) => selectedShifts?.some(x => x.id === shift.id) ?? false;
  
    const isSelectedEmployee = selectedEmployeeId === employee.employeeId;

    const handleExpand = async (event) => {
        await employee.onClickExpand(employee.employeeId);
    };

    const handleChangeIsSkipValidation = async (event) => {
        const checked = event.target.checked;
        dispatch(updateCheckedProposableShifts([]));
        
        await employee.onClickShowAll(employee.employeeId, checked);
    };

    const getStoreDataFromEligibleEmployeeSchedule = (shift) => {
        const shiftSelected = employee.proposableShifts.find(x => x.id === shift.id);
        let storeData = {};
        if (shiftSelected?.isAlreadyOfferedInPartial) {
            storeData.miscellaneousOffers = [{
                alreadyOfferedInPartialStart: shiftSelected.alreadyOfferedInPartialStart,
                alreadyOfferedInPartialEnd: shiftSelected.alreadyOfferedInPartialEnd,
            }];
        }
        return storeData;
    };
    return (
        <>
            <Accordion
                expanded={employee.expanded}
                className="psa-accordion"
                onClick={() => employee.onClick(employee)}
            >
                <IconLeftAccordionSummary
                    expandIcon={<ExpandMore />}
                    disabled={!employee.isValid}
                    IconButtonProps={{
                        edge: 'start',
                        onClick: handleExpand,
                    }}
                >
                    <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
                        <Grid item className={employee.expanded && employee.isPartialShiftEnabled && "psa-smallNameContainer"}>
                            <FormControlLabel 
                                value={employee.employeeId}
                                control={<Radio
                                    color="primary"
                                    disabled={!employee.isValid}
                                    checked={isSelectedEmployee}
                                    onChange={employee.onClickRadioBtn}
                                />
                                }
                                label={<div style={{ display: 'flex', gap: '5px', alignItems: 'center'}}>{`${employee.firstName} ${employee.lastName}`}{!employee.isValidated ? <CircularProgress size={14} /> : null}</div>}
                            />
                        </Grid>

                        {!isReadOnly && employee.expanded && employee.isPartialShiftEnabled &&  (
                            <Grid item className="psa-showAllContainer">
                                <FormControlLabel
                                    className="psa-showAll"
                                    label="Show All"
                                    labelPlacement="start"
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={employee.isSkipValidation}
                                            onChange={handleChangeIsSkipValidation}
                                        />
                                    }
                                />
                                {<Tooltip title="Check to see all the available shifts without the rules being pre-validated. Upon selecting a full or a partial shift, the validation will be done when submitting. This might result in having to submit more than once if the validations fail due to a schedule rule. This is recommended for partial shift requests." arrow>
                                    <HelpIcon fontSize="small" style={{ cursor: "pointer", marginBottom: "7px" }} onClick={employee.handleOpenDrawer} />
                                </Tooltip>}
                            </Grid>
                        )}
                    </Grid>

                    {!employee.isValid ? (
                        <WarningIcon fontSize='larger' style={{ color: '#ffcc00' }} />
                    ) : null}
                </IconLeftAccordionSummary>
                <AccordionDetails className="psa-form-control-container">
                <FormControl className="psa-content-container">
                    {
                        employee.proposableShifts.length
                            ? employee.proposableShifts.map((shift) => (
                                <Card>
                                    <CardContent className="psa-card-padding">
                                        <FormControlLabel
                                            key={shift.id}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name={shift.id}
                                                    value={shift.id}
                                                    checked={isChecked(shift)}
                                                    onChange={(e) => employee.onShiftChecked(e.target.checked, shift, employee)}
                                                    disabled={isCheckboxDisabled && !isChecked(shift)}
                                                />
                                            }
                                            label={<ProposableShiftDateTime shift={shift} />}
                                        />
                                        <ProposableShiftInformation shift={shift} />
                                        {offeredShiftsCount === 1 && isChecked(shift) && employee.isEmployeeCanMakePartials && (
                                            <SliderPartialRequestWithLabel shifts={[shift]}
                                                storeData={getStoreDataFromEligibleEmployeeSchedule(shift)}
                                                requestType={RequestTypes.shiftSwap}
                                                handleChange={employee.handleChangeProposalPartial}
                                                selectedPartial={selectedPartialLabel}
                                                selectedPartialStartDate={selectedPartialStartDate}
                                                selectedPartialEndDate={selectedPartialEndDate}
                                                isReadOnly={isReadOnly}
                                            />
                                        )}
                                    </CardContent>
                                </Card>
                            ))
                            : 'No valid shifts available'
                    }
                </FormControl>
                </AccordionDetails>
            </Accordion>
        </>
    );
}

export default ProposableShiftsAccordion;